/* eslint-disable jsx-a11y/interactive-supports-focus */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { useClipboard } from 'use-clipboard-copy';
import { urlWithFragment } from '@saucelabs/sl-util-helpers';
import { StatusIcon } from '@saucelabs/ds-status';
import {
  IconChevronDownFill,
  IconImageFill,
  IconPerformanceFill,
  IconShareFill,
} from '@saucelabs/ds-icons';
import { Tooltip } from '@saucelabs/ds-tooltip';

import Styles from './JobCommandItemV2.styles';
import PerformanceInfoV2 from './performance-regression/PerformanceInfoV2';
import RegressionAlert from './performance-regression/RegressionAlert';
import { LogTextHighlighter } from './../JobLog';

const context = classNames.bind(Styles);

const JobCommandItemV2 = ({
  active,
  auth,
  collapsed,
  command,
  getPerformanceURL,
  getScreenshotUrl,
  handleClick,
  handleIconClick,
  handlePerformanceClick,
  hideDuration,
  isAdvanceDisabled,
  name,
  onFailedShare,
  onSuccessfulShare,
  screenshotsMapping,
  seleniumSelector,
  showPerformance,
  showLineNumbers,
  rootCauseRef,
}) => {
  const clipboard = useClipboard({
    onSuccess: () => onSuccessfulShare(),
    onError: () => onFailedShare(),
  });

  const triggerCopy = useCallback(() => {
    clipboard.copy(urlWithFragment(window.location.href, command.id));
  }, [clipboard.copy, command]);

  const screenshotUrl = (
    command.hasScreenshot() &&
    getScreenshotUrl(command.getScreenshot(), screenshotsMapping, auth)
  );
  const regressionData = command.get('regressionData');
  const performanceData = command.get('performanceData');
  const rootCauseType = command.get('rootCauseType');

  const onRowClick = () => handleClick(command);

  return (
    <div
      ref={rootCauseType ? rootCauseRef : undefined}
      className="flex relative hover:bg-gray-50 transition duration-150"
      data-active={active || undefined}
      name={name}
    >
      { rootCauseType && <div className="absolute h-full w-0.5 bg-red-900 top-0 left-0">&nbsp;</div> }
      <div
        className={context('flex flex-col w-full', {
          'bg-gray-50': active || !collapsed,
        })}
        onClick={onRowClick}
        role="row"
      >
        <div className={context(
          'header',
          'flex',
          'cursor-pointer',
          'flex-row',
          'pt-1',
          'pr-1',
          'pb-1',
          'pl-0.5',
        )}
        >
          <div className="left flex items-center overflow-hidden">
            <div
              className={context('flex', 'icon', 'p-1.5', 'ml-0.5', {
                'icon-open': !collapsed,
              })}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleIconClick(command);
              }}
              role="button"
              tabIndex="0"
            >
              <IconChevronDownFill
                className="h-2 w-2 fill-current text-gray-600"
              />
            </div>
            {command.isFailed() && (
              <div className="status-icon ml-0 mr-1 flex">
                <StatusIcon
                  type="failed"
                />
              </div>
            )}
            <div className={classNames(
              'command-label',
              'font-semibold',
              'uppercase',
              'ml-0',
              'flex-shrink-0',
              {
                'text-red-900': command.isFailed(),
              })}
            >
              {command.getMethod(seleniumSelector)}
            </div>
            <div className="overflow-ellipsis overflow-hidden ml-1 whitespace-nowrap">{command.getTitle(seleniumSelector)}</div>
          </div>
          <div className="right flex items-center ml-auto space-x-2">
            <Tooltip
              placement="top"
              title="Share this test with this command highlighted"
            >
              <a
                className="flex"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  triggerCopy();
                }}
                role="button"
                tabIndex="0"
              >
                <IconShareFill className={context(
                  'share-icon',
                  'w-2',
                  'h-2',
                  'fill-current',
                  'text-blue-700',
                  {
                    visible: !collapsed,
                    invisible: collapsed,
                  },
                )}
                />
              </a>
            </Tooltip>
            {showPerformance && performanceData && (
              <Tooltip
                placement="top"
                title="View performance report"
              >
                <a
                  className="flex"
                  onClick={handlePerformanceClick}
                  role="presentation"
                >
                  <IconPerformanceFill className="w-2 h-2 fill-current text-blue-700" />
                </a>
              </Tooltip>
            )}
            {command.hasScreenshot() && (
              screenshotUrl ? (
                <Tooltip
                  placement="top"
                  title="View screenshot in new tab"
                >
                  <a
                    className="flex"
                    href={screenshotUrl}
                    referrerPolicy="origin"
                    rel="noopener noreferrer"
                    target="_blank"
                    title="View screenshot in new tab"
                  >
                    <IconImageFill className="w-2 h-2 fill-current text-blue-700" />
                  </a>
                </Tooltip>
              ) : (
                <IconImageFill className="w-2 h-2 fill-current text-blue-700" />
              )
            )}
            <div className={context('flex', 'justify-end', 'duration')}>
              <Tooltip
                placement="top"
                title="Skip to test in video"
              >
                <div
                  className="flex leading-relaxed text-blue-700"
                  onClick={onRowClick}
                  role="button"
                >
                  {command.getTimelineFormatted()}
                </div>
              </Tooltip>
              {!hideDuration && (
                <div className="flex leading-relaxed text-gray-600 ml-0.5">
                  {`(+${command.getDuration()})`}
                </div>
              )}
            </div>
          </div>
        </div>
        {!collapsed && (
          <div className="flex flex-col pl-6 pr-4 pt-0 pb-0 h-auto mb-3">
            <div>
              {regressionData?.is_regression_detected && ( // eslint-disable-line camelcase
                <RegressionAlert
                  getPerformanceURL={getPerformanceURL}
                  regressionData={regressionData}
                />
              )}
              <div className="capitalize text-high-emphasis leading-loose">command</div>
              <div className={context(
                'log',
                'flex',
                'items-center',
                'border',
                'border-solid',
                'border-gray-200',
                'rounded',
                'text-high-emphasis',
                'bg-white',
                'pl-2.5',
                'overflow-y-scroll',
                'h-6',
                'max-h-20',
              )}
              >
                {command.displayPath()}
                {' '}
              </div>
            </div>
            <div>
              <LogTextHighlighter
                className="rounded border-gray-300 border-solid border text-high-emphasis bg-white"
                label="parameters"
                labelClassName="text-sm capitalize text-high-emphasis capitalize leading-loose mt-2"
                showLineNumbers={showLineNumbers}
                text={command.displayRequest()}
              />
            </div>
            <div>
              <LogTextHighlighter
                className="rounded border-gray-300 border-solid border text-high-emphasis bg-white pl-1 pr-1"
                httpStatus={command.displayHttpStatus()}
                label="response"
                labelClassName="text-sm capitalize text-high-emphasis capitalize leading-loose mt-2"
                showLineNumbers={showLineNumbers}
                text={command.displayResult()}
              />
            </div>
            <div>
              {showPerformance && performanceData && (
                <div className="mt-2">
                  <div className="text-high-emphasis leading-loose">Performance</div>
                  <PerformanceInfoV2
                    getPerformanceURL={getPerformanceURL}
                    isAdvanceDisabled={isAdvanceDisabled}
                    onLinkClick={handlePerformanceClick}
                    performanceData={performanceData}
                    regressionData={regressionData}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

JobCommandItemV2.propTypes = {
  active: PropTypes.bool.isRequired,
  auth: PropTypes.string, // HMAC auth param
  collapsed: PropTypes.bool.isRequired,
  command: PropTypes.object.isRequired,
  getPerformanceURL: PropTypes.func,
  getScreenshotUrl: PropTypes.func.isRequired,
  handleClick: PropTypes.func.isRequired,
  handleIconClick: PropTypes.func.isRequired,
  handlePerformanceClick: PropTypes.func.isRequired,
  hideDuration: PropTypes.bool.isRequired,
  isAdvanceDisabled: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onFailedShare: PropTypes.func.isRequired,
  onSuccessfulShare: PropTypes.func.isRequired,
  rootCauseRef: PropTypes.object,
  screenshotsMapping: PropTypes.array,
  seleniumSelector: PropTypes.string,
  showLineNumbers: PropTypes.bool,
  showPerformance: PropTypes.bool.isRequired,
};

JobCommandItemV2.defaultProps = {
  auth: undefined,
  getPerformanceURL: null,
  rootCauseRef: null,
  screenshotsMapping: [],
  seleniumSelector: '',
  showLineNumbers: true,
};

export default JobCommandItemV2;
